// MUNYA OS · #456 · cierre seguro del fondo de los modales
//
// El bug: el fondo llevaba onClick={onClose} y el panel intentaba blindarse con
// onClick={(e) => e.stopPropagation()}. Ese blindaje NO cubre un arrastre: el navegador
// dispara `click` sobre el ANCESTRO COMÚN de mousedown y mouseup. Si el usuario arrastra
// para seleccionar el texto de un campo y suelta fuera del recuadro, ese ancestro es el
// fondo, el panel no está en la ruta del evento, su stopPropagation no llega a ejecutarse
// y onClose() corre: el modal se desmonta y se lleva por delante todo lo tecleado.
//
// El arreglo: el fondo no escucha `click`. Escucha mousedown y mouseup, y solo cierra si
// LOS DOS ocurrieron sobre el propio fondo (target === currentTarget). Cualquier gesto que
// empiece o acabe dentro del panel deja de cerrar.
//
// Además, si el formulario tiene contenido (dirty), cerrar por fondo o por Escape pide
// confirmación en vez de tirar lo escrito.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import { useEscape } from './use-escape.js';

export interface UseDismissableOptions {
  /** Cierre real del modal (el onClose de siempre). */
  onDismiss: () => void;
  /** Si es false el fondo y Escape no cierran (p. ej. mientras se guarda). Por defecto true. */
  enabled?: boolean;
  /** Hay contenido sin guardar: cerrar pide confirmación en vez de descartar en silencio. */
  dirty?: boolean;
  /**
   * Registrar el listener de Escape aquí. Por defecto false para no duplicar el useEscape
   * que ya tienen muchos modales: quien active la guardia de cambios debe pasar `escape: true`
   * y quitar su useEscape, o Escape se saltaría el aviso.
   */
  escape?: boolean;
  confirmTitle?: string;
  confirmBody?: string;
  keepLabel?: string;
  discardLabel?: string;
}

export interface DismissableBackdropProps {
  onMouseDown: (e: ReactMouseEvent) => void;
  onMouseUp: (e: ReactMouseEvent) => void;
}

export interface UseDismissableResult {
  /**
   * Va en el elemento que ES el fondo (el que pinta el velo). Si el overlay tiene un velo
   * hijo `absolute inset-0`, los props van EN EL VELO, no en el contenedor.
   */
  backdropProps: DismissableBackdropProps;
  /** Cierre con guardia: úsalo si quieres que la X o Cancelar también avisen. */
  requestDismiss: () => void;
  /** true mientras se muestra el aviso de cambios sin guardar. */
  discardOpen: boolean;
  /** Cierra el aviso sin descartar nada (para quien gestione Escape por su cuenta). */
  cancelDiscard: () => void;
  /** Aviso «Tienes cambios sin guardar». Se pinta dentro del panel del modal. */
  discardConfirm: ReactElement | null;
}

export function useDismissable(options: UseDismissableOptions): UseDismissableResult {
  const {
    onDismiss,
    enabled = true,
    dirty = false,
    escape = false,
    confirmTitle = 'Tienes cambios sin guardar',
    confirmBody = 'Si cierras ahora se perderá lo que has escrito. ¿Descartar?',
    keepLabel = 'Seguir editando',
    discardLabel = 'Descartar',
  } = options;

  // Se «arma» en el mousedown y solo se dispara si el mouseup cae en el mismo sitio.
  //
  // ⚠️ EL ARMADO SE DECIDE EN CADA GESTO, MIRANDO EL DOCUMENTO ENTERO.
  // Fiar el desarme al onMouseUp del propio fondo solo funciona cuando el panel CUELGA del
  // fondo (ahí el mouseup del panel burbujea y lo limpia). En los ~30 overlays con la forma
  // `<div flex><div velo/><aside panel/></div>` el panel es HERMANO del velo: un mouseup
  // sobre el panel no pasa jamás por el velo, el armado se quedaba pegado y el gesto del
  // ticket (arrastrar dentro de un campo y soltar fuera) volvía a cerrar el modal.
  // Por eso el listener del documento manda: en CADA mousedown el armado se recalcula
  // (solo sobrevive si el fondo acaba de reclamarlo) y en CADA mouseup se apaga.
  const armedRef = useRef(false);
  const claimedRef = useRef(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    // React 19 engancha en el contenedor raíz, así que estos listeners de `window` corren
    // SIEMPRE después del handler del velo: cuando el velo dispara, el armado ya se ha leído.
    const onDocDown = (): void => {
      armedRef.current = claimedRef.current;
      claimedRef.current = false;
    };
    const onDocUp = (): void => {
      armedRef.current = false;
      claimedRef.current = false;
    };
    window.addEventListener('mousedown', onDocDown);
    window.addEventListener('mouseup', onDocUp);
    return () => {
      window.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('mouseup', onDocUp);
    };
  }, []);

  const requestDismiss = useCallback((): void => {
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onDismiss();
  }, [dirty, onDismiss]);

  const onMouseDown = useCallback(
    (e: ReactMouseEvent): void => {
      claimedRef.current = enabled && e.button === 0 && e.target === e.currentTarget;
      // El listener de `window` corre justo después y lo promueve a «armado». Si el gesto
      // empieza en cualquier otro sitio, ese mismo listener deja el armado en false.
    },
    [enabled],
  );

  const onMouseUp = useCallback(
    (e: ReactMouseEvent): void => {
      const armed = armedRef.current;
      armedRef.current = false;
      claimedRef.current = false;
      if (!armed || !enabled || e.button !== 0) return;
      if (e.target !== e.currentTarget) return;
      requestDismiss();
    },
    [enabled, requestDismiss],
  );

  const onEscape = useCallback((): void => {
    if (discardOpen) {
      setDiscardOpen(false);
      return;
    }
    requestDismiss();
  }, [discardOpen, requestDismiss]);
  useEscape(onEscape, escape && enabled);

  const discardConfirm = discardOpen ? (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
      data-testid="mn-discard-confirm"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        className="bg-[var(--color-munya-cream)] rounded-xl shadow-2xl max-w-md w-full p-5"
      >
        <h2 className="font-semibold text-base mb-1">{confirmTitle}</h2>
        <p className="text-sm opacity-80 mb-3">{confirmBody}</p>
        <footer className="flex items-center justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={() => {
              setDiscardOpen(false);
            }}
            className="px-3 py-1.5 text-sm opacity-70 hover:opacity-100"
          >
            {keepLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              setDiscardOpen(false);
              onDismiss();
            }}
            className="px-4 py-1.5 text-sm rounded-md hover:opacity-90 bg-[var(--color-munya-destructive)] text-white"
          >
            {discardLabel}
          </button>
        </footer>
      </div>
    </div>
  ) : null;

  const cancelDiscard = useCallback((): void => {
    setDiscardOpen(false);
  }, []);

  return {
    backdropProps: { onMouseDown, onMouseUp },
    requestDismiss,
    discardOpen,
    cancelDiscard,
    discardConfirm,
  };
}
